import React, { useCallback, useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import _ from "lodash";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import ProcessCostSupportListItem from "./ProcessCostSupportListItem";
import PageContentContainer from "../Container/PageContentContainer";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ApiClient from "../../services/ApiClient";
import PageHeadline from "../PageHeadline/PageHeadline";
import ClearableSearchField from "../Search/ClearableSearchField";
import { Box, SelectChangeEvent } from "@mui/material";
import { processCostSupportListStyle } from "./processCostSupportListStyle";
import Typography from "@mui/material/Typography";
import ProcessCostSupport from "../../types/ProcessCostSupport";

const ProcessCostSupportList = () => {
  const [processCostSupportList, setProcessCostSupportList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [filters, setFilters] = useState({
    page: 1,
    statusFilter: "check",
    nameFilter: "",
  });

  const fetchProcessCostSupportList = useCallback(async () => {
    setIsLoading(true);

    const getUrlParameters = () => {
      let searchParams = new URLSearchParams();
      searchParams.append("page", "" + filters.page);

      if (filters.nameFilter) {
        searchParams.append("petitioner.familyName", filters.nameFilter);
      }

      if (filters.statusFilter !== "all") {
        switch (filters.statusFilter) {
          case "incomplete":
            searchParams.append("leadStatus", "10");
            break;
          case "check":
            searchParams.append("leadStatus", "20");
            break;
          case "done":
            searchParams.append("leadStatus", "30");
            break;
          default:
            break;
        }
      }

      return searchParams;
    };

    const result = await ApiClient.get("process_cost_supports?" + getUrlParameters().toString());
    setProcessCostSupportList(result["hydra:member"]);
    setIsLoading(false);
  }, [filters]);

  useEffect(() => {
    fetchProcessCostSupportList();
  }, [fetchProcessCostSupportList]);

  const handleNameSearchClick = async () => {
    if (!nameFilter) {
      return;
    }

    let newFilters = { ...filters };
    newFilters["nameFilter"] = nameFilter;
    newFilters["page"] = 1;
    setFilters(newFilters);
  };

  const handlePageChange = (page: number) => {
    let newFilters = { ...filters };
    newFilters["page"] = page;
    setFilters(newFilters);
  };

  const handleStatusFilterChange = ({ target }: SelectChangeEvent) => {
    let newFilters = { ...filters };
    newFilters["statusFilter"] = target.value;
    newFilters["page"] = 1;
    setFilters(newFilters);
  };

  const handleClearNameFilter = () => {
    if (!nameFilter && !filters.nameFilter) {
      return;
    }

    let newFilters = { ...filters };
    setNameFilter("");
    newFilters["nameFilter"] = "";
    newFilters["page"] = 1;
    setFilters(newFilters);
  };

  if (isLoading) {
    return (
      <Box sx={processCostSupportListStyle.loading}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageContentContainer size={"small"}>
      <PageHeadline main="Anträge auf Verfahrenskostenhilfe" />
      <Grid
        container
        spacing={4}
        sx={{
          alignItems: "center",
          justifyContent: "flex-end"
        }}>
        <Grid size={5}>
          <ClearableSearchField
            filter={nameFilter}
            placeholder={"Nachname Mandantschaft"}
            setFilter={setNameFilter}
            handleSearchClick={handleNameSearchClick}
            handleClearFilter={handleClearNameFilter}
            showSearchButton
          />
        </Grid>
        <Grid size={2}>
          <Select
            value={filters.statusFilter}
            onChange={handleStatusFilterChange}
            sx={processCostSupportListStyle.filterSelect}
          >
            <MenuItem value={"incomplete"}>Unvollständig</MenuItem>
            <MenuItem value={"check"}>Zu Prüfen</MenuItem>
            <MenuItem value={"done"}>Abgeschlossen</MenuItem>
            <MenuItem value={"all"}>Alle</MenuItem>
          </Select>
        </Grid>
      </Grid>
      <Paper sx={processCostSupportListStyle.paper}>
        {processCostSupportList.length > 0 ? (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Antragsteller / Fall</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Zuletzt bearbeitet am</TableCell>
                <TableCell>Zuständiger</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {_.map(processCostSupportList, (processCostSupport: ProcessCostSupport) => (
                <ProcessCostSupportListItem
                  key={processCostSupport.id}
                  processCostSupport={processCostSupport}
                  updateProcessCostSupports={fetchProcessCostSupportList}
                />
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography sx={processCostSupportListStyle.faded}>Kein Antrag vorhanden.</Typography>
        )}
        <Grid
          container
          spacing={4}
          sx={[{
            justifyContent: "space-between"
          }, processCostSupportListStyle.buttonContainer]}>
          <Grid>
            <Button
              disabled={filters.page === 1}
              onClick={() => {
                handlePageChange(filters.page - 1);
              }}
            >
              &laquo; vorherige Seite
            </Button>
          </Grid>
          <Grid>
            <Button
              disabled={processCostSupportList && processCostSupportList.length < 10}
              onClick={() => {
                handlePageChange(filters.page + 1);
              }}
            >
              nächste Seite &raquo;
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </PageContentContainer>
  );
};

export default ProcessCostSupportList;
